// src/components/Forum/UserPosts.jsx
import React, { useState, useEffect } from 'react';
import { getUserPosts, updatePostStatus, deletePost } from '../../services/forumApi';
import { deletePhoto } from '../../storageService';
import { useAuth } from '../../utils/AuthContext';
import { toast } from 'react-toastify';
import { MessageSquare, Clock, MapPin, Phone, Trash2 } from 'lucide-react';
import Spinner from '../Spinner/Spinner';
import './UserPosts.css';

const UserPosts = () => {
    const { user, loading: authLoading } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState({});
    const [deleting, setDeleting] = useState({});
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [postToDelete, setPostToDelete] = useState(null);

    useEffect(() => {
        if (user) {
            loadUserPosts();
        } else if (!authLoading) {
            setLoading(false);
        }
    }, [user, authLoading]);

    const loadUserPosts = async () => {
        try {
            if (!user) {
                setLoading(false);
                return;
            }

            console.log('📧 Cargando posts para el usuario:', user);

            // getUserPosts ya no recibe parámetros, usa el token internamente
            const response = await getUserPosts();

            // El backend devuelve {posts: [], total: number}
            const userPosts = response.posts || [];

            // Asegurar que siempre sea un array
            setPosts(Array.isArray(userPosts) ? userPosts : []);

        } catch (error) {
            console.error('Error loading user posts:', error);
            toast.error('Error al cargar tus posts');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (postId, newStatus) => {
        setUpdating(prev => ({ ...prev, [postId]: true }));

        try {
            console.log(`🔄 Actualizando estado del post ${postId} a: ${newStatus}`);

            const result = await updatePostStatus(postId, { status: newStatus });
            console.log('✅ Estado actualizado exitosamente:', result);

            setPosts(prev => {
                const safePrev = Array.isArray(prev) ? prev : [];
                return safePrev.map(post =>
                    post.id === postId
                        ? { ...post, status: newStatus }
                        : post
                );
            });

            toast.success('Estado del post actualizado correctamente');

        } catch (error) {
            console.error('❌ Error updating post status:', error);
            console.error('Error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                message: error.message
            });

            // Manejo específico de errores
            if (error.response?.status === 400) {
                const errorMessage = error.response?.data?.message || 'Estado no válido';
                toast.error(`Error: ${errorMessage}`);
            } else if (error.response?.status === 401) {
                toast.error('No autorizado para actualizar este post');
            } else if (error.response?.status === 404) {
                toast.error('Post no encontrado');
            } else {
                toast.error('Error al actualizar el estado del post');
            }
        } finally {
            setUpdating(prev => ({ ...prev, [postId]: false }));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === 'Sin fecha') {
            return 'Sin fecha';
        }

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Sin fecha';
            }

            return date.toLocaleDateString('es-AR', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Sin fecha';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE': return '#28a745';
            case 'PAUSED': return '#ffc107';
            case 'RESOLVED': return '#17a2b8';
            case 'CLOSED': return '#6c757d';
            case 'EXPIRED': return '#fd7e14';
            default: return '#28a745';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'ACTIVE': return 'Activo';
            case 'PAUSED': return 'Pausado';
            case 'RESOLVED': return 'Resuelto';
            case 'CLOSED': return 'Cerrado';
            case 'EXPIRED': return 'Expirado';
            default: return 'Activo';
        }
    };

    // Eliminar múltiples imágenes usando deletePhoto individualmente
    const deleteMultipleImages = async (imagePaths) => {
        if (!imagePaths || imagePaths.length === 0) return true;

        try {
            // Llamar deletePhoto para cada imagen
            const deletePromises = imagePaths.map(async (imagePath) => {
                try {
                    await deletePhoto(imagePath);
                    return true;
                } catch (error) {
                    console.error(`Error deleting image ${imagePath}:`, error);
                    return false;
                }
            });

            const results = await Promise.all(deletePromises);
            return results.every(result => result === true);
        } catch (error) {
            console.error('Error deleting multiple images:', error);
            return false;
        }
    };

    const handleDeletePost = async (post) => {
        // Abrir modal de confirmación personalizado
        setPostToDelete(post);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!postToDelete) return;

        setDeleting(prev => ({ ...prev, [postToDelete.id]: true }));

        try {
            // Si el post tiene imágenes, eliminarlas primero
            if (postToDelete.images && postToDelete.images.length > 0) {
                console.log('🗑️ Eliminando imágenes del post:', postToDelete.images);
                const imagesDeleted = await deleteMultipleImages(postToDelete.images);

                if (!imagesDeleted) {
                    console.warn('⚠️ Algunas imágenes no pudieron ser eliminadas, pero continuando con la eliminación del post');
                }
            }

            console.log(`🗑️ Eliminando post del backend: ${postToDelete.id}`);

            // Eliminar el post del backend
            const result = await deletePost(postToDelete.id);
            console.log('✅ Post eliminado exitosamente:', result);

            // Actualizar el estado local para remover el post eliminado
            setPosts(prev => {
                const safePrev = Array.isArray(prev) ? prev : [];
                return safePrev.filter(p => p.id !== postToDelete.id);
            });

            toast.success('Post eliminado correctamente');

        } catch (error) {
            console.error('❌ Error deleting post:', error);
            console.error('Error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                message: error.message
            });

            // Manejo específico de errores según la documentación del backend
            if (error.response?.status === 400) {
                const errorMessage = error.response?.data?.message;
                if (errorMessage?.includes('Solo el autor puede eliminar')) {
                    toast.error('No tienes permisos para eliminar este post');
                } else if (errorMessage?.includes('Post no encontrado')) {
                    toast.error('El post ya no existe');
                    // Remover del estado local si ya no existe en el backend
                    setPosts(prev => {
                        const safePrev = Array.isArray(prev) ? prev : [];
                        return safePrev.filter(p => p.id !== postToDelete.id);
                    });
                } else {
                    toast.error(`Error: ${errorMessage || 'No se pudo eliminar el post'}`);
                }
            } else if (error.response?.status === 401) {
                toast.error('Tu sesión ha expirado. Por favor inicia sesión nuevamente.');
            } else if (error.response?.status === 404) {
                toast.error('El post ya no existe');
                // Remover del estado local si ya no existe en el backend
                setPosts(prev => {
                    const safePrev = Array.isArray(prev) ? prev : [];
                    return safePrev.filter(p => p.id !== postToDelete.id);
                });
            } else {
                toast.error('Error al eliminar el post. Inténtalo nuevamente.');
            }
        } finally {
            setDeleting(prev => ({ ...prev, [postToDelete.id]: false }));
            setDeleteModalOpen(false);
            setPostToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
        setPostToDelete(null);
    };

    if (loading) {
        return <Spinner />;
    }

    // Asegurar que posts sea siempre un array
    const safePosts = Array.isArray(posts) ? posts : [];

    if (safePosts.length === 0) {
        return (
            <div className="user-posts-container">
                <h3>Mis Posts</h3>
                <div className="no-posts">
                    <MessageSquare size={48} />
                    <p>Aún no has creado ningún post</p>
                    <p className="no-posts-help">
                        Crea un post para describir un problema y conectar con proveedores especializados
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="user-posts-container">
            <h3>Mis Posts ({safePosts.length})</h3>

            <div className="posts-list">
                {safePosts.map(post => (
                    <div key={post.id} className="user-post-card">
                        <div className="post-header">
                            <div className="post-status-date">
                                <span
                                    className="status-badge"
                                    style={{ backgroundColor: getStatusColor(post.status) }}
                                >
                                    {getStatusText(post.status)}
                                </span>
                                <div className="post-date">
                                    <Clock size={14} />
                                    {formatDate(post.createdAt || post.createdDate || 'Sin fecha')}
                                </div>
                            </div>

                                <div className="post-actions">
                                    {post.status === 'PAUSED' && (
                                        <button
                                            type="button"
                                            onClick={() => handleStatusChange(post.id, 'ACTIVE')}
                                            disabled={updating[post.id]}
                                            className="status-select"
                                            style={{
                                                background: '#28a745',
                                                color: '#fff',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontWeight: 600
                                            }}
                                        >
                                            {updating[post.id] ? 'Reactivando...' : 'Reactivar post'}
                                        </button>
                                    )}
                                    <select
                                        value={post.status}
                                        onChange={(e) => handleStatusChange(post.id, e.target.value)}
                                        disabled={updating[post.id]}
                                        className="status-select"
                                    >
                                        <option value="ACTIVE">Activo</option>
                                        <option value="PAUSED">Pausado</option>
                                        <option value="RESOLVED">Resuelto</option>
                                        <option value="CLOSED">Cerrado</option>
                                        <option value="EXPIRED">Expirado</option>
                                    </select>
                                </div>
                        </div>

                        {post.status === 'PAUSED' && (
                            <div style={{
                                background: '#fff3cd',
                                border: '1px solid #ffeeba',
                                color: '#856404',
                                padding: '8px 12px',
                                borderRadius: 6,
                                fontSize: 13,
                                margin: '8px 0'
                            }}>
                                Este post fue pausado automáticamente porque ya recibió varios contactos. Reactivalo cuando quieras seguir recibiendo proveedores.
                            </div>
                        )}

                        <div className="post-content">
                            {post.title && (
                                <h4 className="post-title">{post.title}</h4>
                            )}

                            <p className="post-description">
                                {post.description}
                            </p>

                            <div className="post-details">
                                {post.serviceLocationId && (
                                    <div className="detail-item">
                                        <MapPin size={14} />
                                        <span>{post.serviceLocationId}</span>
                                    </div>
                                )}

                                <div className="detail-item">
                                    <MessageSquare size={14} />
                                    <span>{post.totalInterested || 0} interesado{(post.totalInterested || 0) !== 1 ? 's' : ''}</span>
                                </div>

                                {post.authorPhone && (
                                    <div className="detail-item">
                                        <Phone size={14} />
                                        <span>Teléfono incluido</span>
                                    </div>
                                )}
                            </div>

                            {post.categories && post.categories.length > 0 && (
                                <div className="post-categories">
                                    {post.categories.map((category, index) => (
                                        <span key={index} className="category-tag">
                                            {category}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {(post.totalInterested || 0) > 0 && (
                            <div className="interests-summary">
                                <p className="interests-text">
                                    <strong>{post.totalInterested}</strong> proveedor{post.totalInterested !== 1 ? 'es han' : ' ha'} mostrado interés en este post
                                </p>
                            </div>
                        )}

                        <div className="post-footer">
                            <button
                                onClick={() => handleDeletePost(post)}
                                disabled={deleting[post.id]}
                                className="delete-post-button"
                            >
                                <Trash2 size={16} />
                                {deleting[post.id] ? 'Eliminando...' : 'Eliminar Post'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal de confirmación personalizado */}
            {deleteModalOpen && postToDelete && (
                <div className="delete-confirmation-overlay" onClick={handleCancelDelete}>
                    <div className="delete-confirmation-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-modal-header">
                            <div className="delete-modal-icon">
                                <Trash2 size={20} />
                            </div>
                            <h3 className="delete-modal-title">Eliminar Post</h3>
                        </div>

                        <p className="delete-modal-message">
                            ¿Estás seguro de que deseas eliminar este post? Esta acción no se puede deshacer.
                        </p>

                        <div className="delete-post-preview">
                            <div className="delete-post-preview-title">
                                {postToDelete.title || 'Post sin título'}
                            </div>
                            <p className="delete-post-preview-text">
                                {postToDelete.description?.substring(0, 100)}
                                {postToDelete.description?.length > 100 ? '...' : ''}
                            </p>
                        </div>

                        <div className="delete-modal-actions">
                            <button
                                onClick={handleCancelDelete}
                                className="delete-modal-button delete-modal-cancel"
                                disabled={deleting[postToDelete.id]}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                className="delete-modal-button delete-modal-confirm"
                                disabled={deleting[postToDelete.id]}
                            >
                                <Trash2 size={16} />
                                {deleting[postToDelete.id] ? 'Eliminando...' : 'Eliminar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserPosts;
